import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";

import { useTracking } from "utilities/analytics/analytics";

import { EXCLUDED_ENTITIES, getEntityLink } from "../AiSearchResults";
import { getHitKey, normalizeKey } from "../entityMeta";
import { mergeResultsRRF } from "../rrf";
import type { EntityScope, IAiSearchState, ITrackedOp, SearchStreamEvent } from "../types";
import { useAuthToken } from "./useAuthToken";

/**
 * Hook for SSE-based AI search against the BTNet-Search service.
 *
 * Streams results progressively as they arrive, merges them with RRF,
 * and surfaces QA answers for question-type queries.
 *
 * Usage:
 *   const { doSearch, state, cancel } = useAiSearch();
 *   doSearch("foundation repair contractors");
 */
export function useAiSearch() {
    const appDefaultInfo = useContext(AppDefaultInfoContext);
    const searchConfig = appDefaultInfo?.searchServiceConfiguration;
    const searchBaseUrl = searchConfig?.apiBaseUrl ?? "";
    const searchAudience = searchConfig?.authAudience ?? "";

    const { getToken, clearToken } = useAuthToken(searchAudience);
    const { trackEvent } = useTracking({ component: "AI Search" });
    const abortControllerRef = useRef<AbortController | null>(null);
    // Monotonic sequence number bumped on each search/cancel. Used only to
    // discard stale SSE events from a superseded request — unrelated to the
    // telemetry correlation uuid (state.searchId / searchUuidRef).
    const searchSequenceIdRef = useRef(0);
    // The uuid correlation id for the in-flight search. Mirrors state.searchId but
    // is reachable from handleEvent without adding it to the callback's deps.
    const searchUuidRef = useRef("");

    // Refs for entity scope (avoids stale closures in handleEvent)
    const entityScopeRef = useRef<EntityScope>("broad");
    const scopedEntitiesRef = useRef<Set<string>>(new Set());

    // Dead-link hits already reported for the current search. Caps per-search
    // telemetry volume at O(unique dead hits) instead of O(SSE batches × hits),
    // so RRF re-merges of the same hit across batches don't multiply events.
    const reportedDeadLinksRef = useRef<Set<string>>(new Set());

    // Keep trackEvent reachable from handleEvent without forcing its deps to change.
    const trackEventRef = useRef(trackEvent);
    useEffect(() => {
        trackEventRef.current = trackEvent;
    }, [trackEvent]);

    const [state, setState] = useState<IAiSearchState>({
        searchId: "",
        searchQuery: "",
        results: [],
        staleResults: [],
        operations: [],
        qaAnswers: {},
        entityScope: "broad",
        scopedEntities: new Set(),
        isQuestion: false,
        phase: "idle",
        intentReceived: false,
        error: null,
        searchStartTime: null,
    });

    const handleEvent = useCallback((event: SearchStreamEvent, searchSequenceId: number) => {
        if (searchSequenceId !== searchSequenceIdRef.current) return;

        switch (event.type) {
            case "intent": {
                const scope = event.entity_scope ?? "broad";
                const scoped = new Set<string>(event.scoped_entities ?? []);

                entityScopeRef.current = scope;
                scopedEntitiesRef.current = scoped;

                setState((prev) => ({
                    ...prev,
                    intentReceived: true,
                    entityScope: scope,
                    scopedEntities: scoped,
                    isQuestion: event.is_question === true,
                    operations: [
                        ...prev.operations,
                        ...(Array.isArray(event.plans)
                            ? event.plans.map(
                                  (plan): ITrackedOp => ({
                                      ...plan,
                                      stage: "extra" as const,
                                      status: "pending" as const,
                                  }),
                              )
                            : []),
                    ],
                }));
                break;
            }

            case "result": {
                const resultData = event.results;
                if (!resultData) break;

                const stage = event.stage ?? "default";
                const op = { ...resultData.op, stage };
                const result = { ...resultData, op };

                // Tracks hits the frontend can't route to (missing payload fields
                // or unhandled entity types) so we can monitor the backend payload
                // gap. Dedupes per search to prevent multiple events for the same hit.
                for (const hit of result.hits) {
                    if (EXCLUDED_ENTITIES.has(normalizeKey(hit.entity))) continue;
                    if (getEntityLink(hit) !== null) continue;
                    const key = getHitKey(hit);
                    if (reportedDeadLinksRef.current.has(key)) continue;
                    reportedDeadLinksRef.current.add(key);
                    trackEventRef.current({
                        event: "Search",
                        extraInfo: {
                            searchId: searchUuidRef.current,
                            searchPhase: "dead-link-dropped",
                            entity: hit.entity,
                            id: hit.id,
                            payloadKeys: Object.keys(hit.payload ?? {}),
                        },
                    });
                }

                setState((prev) => {
                    const nextPhase = prev.phase === "loading" ? "streaming" : prev.phase;
                    const existing = prev.operations.find((o) => o.id === op.id);
                    const status: ITrackedOp["status"] = result.error ? "error" : "complete";
                    const updatedOps = existing
                        ? prev.operations.map((o) =>
                              o.id === op.id
                                  ? {
                                        ...o,
                                        stage,
                                        status,
                                        resultCount: result.hits.length,
                                        elapsed_ms: result.elapsed_ms,
                                    }
                                  : o,
                          )
                        : [
                              ...prev.operations,
                              {
                                  ...op,
                                  status,
                                  resultCount: result.hits.length,
                                  elapsed_ms: result.elapsed_ms,
                              },
                          ];

                    return {
                        ...prev,
                        phase: nextPhase,
                        staleResults: [],
                        operations: updatedOps,
                        results: mergeResultsRRF(
                            prev.results,
                            result,
                            entityScopeRef.current,
                            scopedEntitiesRef.current,
                        ),
                    };
                });
                break;
            }

            case "qa": {
                const source = event.source;
                const answer = {
                    ...event.answer,
                    source,
                    _elapsed: event._elapsed,
                };
                setState((prev) => ({
                    ...prev,
                    qaAnswers: { ...prev.qaAnswers, [source]: answer },
                }));
                break;
            }

            case "error": {
                setState((prev) => ({
                    ...prev,
                    phase: "error",
                    error: event.error ?? "Search error",
                }));
                break;
            }

            case "done": {
                setState((prev) => ({
                    ...prev,
                    staleResults: [],
                    phase: prev.results.length > 0 ? "done" : "empty",
                }));
                break;
            }
        }
    }, []);

    const fetchSSE = useCallback(
        async (query: string, signal: AbortSignal, searchSequenceId: number) => {
            let token: string;
            try {
                token = await getToken();
            } catch {
                if (searchSequenceId !== searchSequenceIdRef.current) return;
                setState((prev) => ({
                    ...prev,
                    error: "Failed to authenticate with search service",
                    phase: "error",
                }));
                return;
            }

            try {
                const response = await fetch(`${searchBaseUrl}v1/search`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ query, search_id: searchUuidRef.current }),
                    signal,
                });

                if (searchSequenceId !== searchSequenceIdRef.current) return;

                if (response.status === 401) {
                    clearToken();
                    setState((prev) => ({
                        ...prev,
                        error: "Authentication expired. Please try again.",
                        phase: "error",
                    }));
                    return;
                }

                if (!response.ok || !response.body) {
                    setState((prev) => ({
                        ...prev,
                        error: `Search failed (${response.status})`,
                        phase: "error",
                    }));
                    return;
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";
                let currentEvent = "";

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split("\n");
                    buffer = lines.pop()!;

                    for (const line of lines) {
                        if (line.startsWith("event: ")) {
                            currentEvent = line.slice(7).trim();
                        } else if (line.startsWith("data: ")) {
                            try {
                                const eventData = JSON.parse(line.slice(6)) as Record<
                                    string,
                                    unknown
                                >;
                                handleEvent(
                                    {
                                        type: currentEvent,
                                        ...eventData,
                                    } as SearchStreamEvent,
                                    searchSequenceId,
                                );
                            } catch {
                                // Skip malformed JSON
                            }
                            currentEvent = "";
                        }
                    }
                }
            } catch (err: unknown) {
                if (err instanceof DOMException && err.name === "AbortError") {
                    return;
                }
                if (searchSequenceId !== searchSequenceIdRef.current) return;
                setState((prev) => ({
                    ...prev,
                    error: "Connection to search service failed",
                    phase: "error",
                }));
            }
        },
        [searchBaseUrl, getToken, clearToken, handleEvent],
    );

    const doSearch = useCallback(
        (query: string) => {
            if (!searchBaseUrl) {
                setState((prev) => ({
                    ...prev,
                    error: "Search service URL not configured",
                }));
                return;
            }

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            const controller = new AbortController();
            abortControllerRef.current = controller;

            searchSequenceIdRef.current += 1;
            const searchSequenceId = searchSequenceIdRef.current;

            const searchUuid = uuidv4();
            searchUuidRef.current = searchUuid;

            entityScopeRef.current = "broad";
            scopedEntitiesRef.current = new Set();
            reportedDeadLinksRef.current = new Set();

            setState((prev) => ({
                searchId: searchUuid,
                searchQuery: query,
                results: [],
                staleResults: prev.results,
                operations: [],
                qaAnswers: {},
                entityScope: "broad",
                scopedEntities: new Set(),
                isQuestion: false,
                phase: "loading",
                intentReceived: false,
                error: null,
                searchStartTime: Date.now(),
            }));

            void fetchSSE(query, controller.signal, searchSequenceId);
        },
        [searchBaseUrl, fetchSSE],
    );

    const cancel = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        // Invalidate any late SSE events still flushing through handleEvent
        // so they can't setState({phase: "streaming"}) after we've gone idle.
        searchSequenceIdRef.current += 1;
        setState((prev) => ({
            ...prev,
            staleResults: [],
            phase: "idle",
        }));
    }, []);

    return { doSearch, cancel, state };
}
